import React, { useState, useEffect, useContext } from 'react';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBackspace } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';

import plataformasContext from '../../context/plataformas/plataformasContext';
import plataformaTenantContext from '../../context/plataformaTenant/plataformaTenantContext';

import './PlataformaTenantSubtabla.css';

const PlataformaTenantSubtabla = ({ tenantId, tablaSeleccionada }) => {
    const pContext = useContext(plataformasContext);
    const { plataformas, getAllPlataformas } = pContext;

    const ptContext = useContext(plataformaTenantContext);
    const {
        plataformasByTenant,
        getPlataformasByTenantId,
        addPlataformaTenant,
        updatePlataformaTenant,
        deletePlataformaTenant
    } = ptContext;

    // Estado local para manejar las filas (incluyendo la fila vacía para agregar)
    const [rows, setRows] = useState([]);

    useEffect(() => {
        getAllPlataformas();
    }, []);

    useEffect(() => {
        if (tenantId) {
            getPlataformasByTenantId(tenantId);
        }
    }, [tenantId]);

    useEffect(() => {
        // Cuando cambian las plataformas del backend, actualizar las filas
        if (plataformasByTenant) {
            const existingRows = plataformasByTenant.map(pt => ({
                _id: pt._id,
                plataforma: pt.plataformaId ? {
                    value: pt.plataformaId._id,
                    label: `${pt.plataformaId.codPlataforma} - ${pt.plataformaId.name}`
                } : null,
                urlBackend: pt.urlBackend || '',
                port: pt.port || '',
                realTimeServerUrl: pt.realTimeServerUrl || '',
                realTimeServerPort: pt.realTimeServerPort || '',
                isNew: false,
                hasChanges: false
            }));
            // Agregar fila vacía al final
            setRows([...existingRows, createEmptyRow()]);
        }
    }, [plataformasByTenant]);

    const createEmptyRow = () => ({
        _id: null,
        plataforma: null,
        urlBackend: '',
        port: null,
        realTimeServerUrl: '',
        realTimeServerPort: null,
        isNew: true,
        hasChanges: false
    });

    const plataformaOptions = plataformas
        ?.filter(p => p.enabled)
        .map(p => ({
            value: p._id,
            label: `${p.codPlataforma} - ${p.name}`
        })) || [];

    const handleSelectChange = async (selectedOption, index) => {
        const row = rows[index];

        if (row.isNew && selectedOption) {
            // Es una fila nueva, crear en el backend
            try {
                await addPlataformaTenant({
                    tenantId: tenantId,
                    plataformaId: selectedOption.value,
                    urlBackend: row.urlBackend,
                    enabled: true
                });
                await getPlataformasByTenantId(tenantId);
            } catch (error) {
                Swal.fire('Error', error.message || 'Error al agregar plataforma', 'error');
            }
        } else if (!row.isNew && selectedOption) {
            // Actualizar plataforma existente (cambio de plataforma)
            try {
                await updatePlataformaTenant(row._id, {
                    plataformaId: selectedOption.value
                });
                await getPlataformasByTenantId(tenantId);
            } catch (error) {
                Swal.fire('Error', error.message || 'Error al actualizar plataforma', 'error');
            }
        }
    };

    const handleInputChange = async (index, field, value) => {
        const row = rows[index];

        // Actualizar estado local
        const newRows = [...rows];
        newRows[index] = { ...newRows[index], [field]: value, hasChanges: true };
        setRows(newRows);
    };

    const handleInputBlur = async (index, field) => {
        const row = rows[index];

        if (!row.isNew && row._id && row.hasChanges) {
            // Guardar cambios al perder foco
            try {
                await updatePlataformaTenant(row._id, {
                    urlBackend: row.urlBackend,
                    port: row.port,
                    realTimeServerUrl: row.realTimeServerUrl,
                    realTimeServerPort: row.realTimeServerPort
                });

                const newRows = [...rows];
                
                newRows[index].hasChanges = false;

                setRows(newRows);
            } catch (error) {
                console.error('Error al actualizar:', error);
            }
        }
    };

    const handleDelete = async (index) => {
        const row = rows[index];

        if (row.isNew || !row.plataforma) {
            return; // No hacer nada si es fila vacía
        }

        Swal.fire({
            title: `¿Está seguro que desea eliminar esta plataforma?`,
            showDenyButton: true,
            confirmButtonText: 'Sí, eliminar',
            confirmButtonColor: '#ff0000',
            denyButtonColor: '#3d4d87',
            denyButtonText: 'No',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await deletePlataformaTenant({ _id: row._id });
                    await getPlataformasByTenantId(tenantId);
                    Swal.fire('Eliminado', 'Plataforma eliminada correctamente', 'success');
                } catch (error) {
                    Swal.fire('Error', error.message || 'Error al eliminar plataforma', 'error');
                }
            }
        });
    };

    if (!tenantId) {
        return (
            <div className={`${tablaSeleccionada !== 'plataformas' ? 'inactive' : ''} plataformasDiv`}>
                <p className="noPlataformasMsg">Guarde el tenant primero para poder agregar plataformas</p>
            </div>
        );
    }

    return (
        <div className={`${tablaSeleccionada !== 'plataformas' ? 'inactive' : ''} plataformasDiv`}>
            <label className="plataformasLabel">Plataformas por Tenant</label>
            <div className="plataformasXtenant">
                {rows.map((row, index) => (
                    <div className="plataformaDiv" key={row._id || `new-${index}`}>
                        <FontAwesomeIcon
                            icon={faBackspace}
                            color="red"
                            style={{ width: '60px', height: '30px', marginTop: '5px', cursor: row.plataforma ? 'pointer' : 'default', opacity: row.plataforma ? 1 : 0.3 }}
                            onClick={() => handleDelete(index)}
                        />

                        <div className="selectControlDiv">
                            <Select
                                placeholder="Seleccione una plataforma..."
                                options={plataformaOptions}
                                value={row.plataforma}
                                onChange={(selected) => handleSelectChange(selected, index)}
                                isDisabled={!row.isNew && row.plataforma}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    container: (base) => ({ ...base, flex: 1 }),
                                    control: (base) => ({ ...base, minHeight: '38px' })
                                }}
                            />
                        </div>

                        <input
                            className="inputDescrp"
                            placeholder="URL Backend"
                            value={row.urlBackend}
                            onChange={(e) => handleInputChange(index, 'urlBackend', e.target.value)}
                            onBlur={() => handleInputBlur(index, 'urlBackend')}
                            disabled={!row.plataforma}
                        />
                        
                        <input
                            className="inputDescrp"
                            placeholder="Puerto Backend (Vacio si no corresponde)"
                            value={row.port}
                            type='number'
                            onChange={(e) => handleInputChange(index, 'port', e.target.value)}
                            onBlur={() => handleInputBlur(index, 'port')}
                            disabled={!row.plataforma}
                        />

                        <input
                            className="inputDescrp"
                            placeholder="URL Real Time Server"
                            value={row.realTimeServerUrl}
                            onChange={(e) => handleInputChange(index, 'realTimeServerUrl', e.target.value)}
                            onBlur={() => handleInputBlur(index, 'realTimeServerUrl')}
                            disabled={!row.plataforma}
                        />

                        <input
                            className="inputDescrp"
                            placeholder="Puerto Real Time Server"
                            value={row.realTimeServerPort}
                            type='number'
                            onChange={(e) => handleInputChange(index, 'realTimeServerPort', e.target.value)}
                            onBlur={() => handleInputBlur(index, 'realTimeServerPort')}
                            disabled={!row.plataforma}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

export default PlataformaTenantSubtabla;
